import i18nInstance from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * i18n bootstrap.
 * - Loads every `locales/<lang>/<namespace>.json` at build time via Vite's
 * eager glob import — no runtime fetches, no chunking surprises.
 * - Language is hard-pinned to English (`lng: 'en'`) for now:
 * the portal must show English regardless of the browser's default locale.
 * The other language packs are still bundled, so re-enabling locale
 * detection later is just a matter of restoring the `LanguageDetector`
 * plugin + `detection` options and dropping the explicit `lng`.
 * - Supported languages: en (default), fr, es, pt — matches portal's set.
 * - Namespaces are kept thin and scoped: `common` for generic actions,
 * `navigation` for sidebar/topbar, `preview` for the Phase 0.5 demo,
 * `orders` for the Orders domain. Add new feature namespaces in lock-step
 * with the feature folder (Phase 2+).
 */

const modules = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../locales/*/*.json',
  { eager: true },
);

export const supportedLanguages = ['en', 'fr', 'es', 'pt'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

const namespaces = ['common', 'navigation', 'preview', 'orders', 'auth', 'home', 'merchants', 'vendors', 'plugins', 'catalog', 'routing', 'routing-v3', 'shipments', 'users', 'role', 'webhooks', 'skuMappings', 'shippingGroups'] as const;
export type Namespace = (typeof namespaces)[number];

type Resources = Record<string, Record<string, Record<string, unknown>>>;

const resources: Resources = {};

for (const lang of supportedLanguages) {
  resources[lang] = {};
  for (const ns of namespaces) {
    const path = `../../locales/${lang}/${ns}.json`;
    const mod = modules[path];
    if (mod) {
      resources[lang][ns] = mod.default;
    }
  }
}

void i18nInstance
.use(initReactI18next)
.init({
    resources,
    // Hard-pin to English for now. No LanguageDetector, so the
    // browser locale is never consulted.
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: [...supportedLanguages],
    ns: [...namespaces],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
  });

export const i18n = i18nInstance;
